import {
  CompanyConfig,
  getActiveCompanyConfig,
} from "domain/company/companyConfig";

import {
  Context,
  Intersection,
  Line,
  Pannel,
  Substraction,
} from "types/canvasTypes";

import {
  calculateLineLength,
  calculateNumberOfGlass,
  calculateNumberOfGlassBasedOnBiggestGlass,
} from "./geometry";
import {
  checkLineIntersection,
  getAllIntersections,
  GlassAngle,
} from "./intersections";

function findSubstractionsBasedOnAngle(glass: GlassAngle): number {
  if (glass.internal) {
    if (glass.angle < 30) return 6;
    if (glass.angle < 75) return 5;
    if (glass.angle < 100) return 4;
    if (glass.angle < 120) return 3;
    if (glass.angle < 140) return 2;
    return 1;
  }

  if (glass.angle < 30) return -4;
  if (glass.angle < 75) return -3;
  if (glass.angle < 100) return -2;
  if (glass.angle < 120) return -1;
  if (glass.angle < 140) return -0.5;
  return 1;
}

function findSubstractions(intersections: Intersection[]): Substraction[] {
  const substractions: Substraction[] = [];

  for (let i = 0; i < intersections.length; i++) {
    const current = intersections[i];
    const newSubstraction: Substraction = {
      line: current.line,
      substractions: 0,
    };

    if (current.intersections.length === 0) {
      newSubstraction.substractions = 2;
    } else if (current.intersections.length === 1) {
      const glass = checkLineIntersection(
        current.line.parallelLine!,
        current.intersections[0].parallelLine!
      );
      newSubstraction.substractions = findSubstractionsBasedOnAngle(glass) + 1;
    } else if (current.intersections.length === 2) {
      const glass1 = checkLineIntersection(
        current.line.parallelLine!,
        current.intersections[0].parallelLine!
      );
      const glass2 = checkLineIntersection(
        current.line.parallelLine!,
        current.intersections[1].parallelLine!
      );
      newSubstraction.substractions =
        findSubstractionsBasedOnAngle(glass1) +
        findSubstractionsBasedOnAngle(glass2);
    }

    substractions.push(newSubstraction);
  }

  return substractions;
}

function calculateCombinationIdeal(
  totalNumberOfGlassNeeded: number,
  closest: number,
  secondClosest: number,
  totalLength: number
): number {
  const allCombinationsSum: number[] = [];
  const numberOfIdeaGlass: number[] = [];

  for (
    let numberOfIdealNeeded = 0;
    numberOfIdealNeeded <= totalNumberOfGlassNeeded;
    numberOfIdealNeeded += 1
  ) {
    const numberOfSecondGlass = totalNumberOfGlassNeeded - numberOfIdealNeeded;
    const sum =
      numberOfIdealNeeded * closest + numberOfSecondGlass * secondClosest;

    if (totalLength - sum > -0.1 * totalNumberOfGlassNeeded) {
      allCombinationsSum.push(Math.abs(totalLength - sum));
      numberOfIdeaGlass.push(numberOfIdealNeeded);
    }
  }

  const minimumSum = Math.min(...allCombinationsSum);
  return numberOfIdeaGlass[allCombinationsSum.indexOf(minimumSum)];
}

function calculateBounds(
  perfectglass: number,
  glassList: Pannel[]
): [Pannel | null, Pannel | null] {
  let upperBound: Pannel | null = null;
  let lowerBound: Pannel | null = null;

  for (const value of glassList) {
    if (value.length >= perfectglass) {
      upperBound = value;
      break;
    }
    lowerBound = value;
  }

  return [lowerBound, upperBound];
}

function get48Pannels(
  line: Line,
  scale: number,
  glassDeduction: number,
  gapSize: number,
  companyConfig: CompanyConfig
): Pannel[] {
  const length = calculateLineLength(line, scale);
  const numberOfGlass = calculateNumberOfGlass(line, scale, companyConfig);
  const h48List = companyConfig.catalog.glassPanels.h48;
  const r48List = companyConfig.catalog.glassPanels.r48;

  if (!length) {
    throw Error("length is undefined");
  }

  let tempLength = length - companyConfig.catalog.doorWidth * line.numberOfDoors;
  const spaceNeeded = numberOfGlass * gapSize - 1 - (line.numberOfDoors > 0 ? 1 : 0);
  const deduction = spaceNeeded + glassDeduction;
  const totalLengthNeeded = tempLength - deduction;
  const idealGlass = totalLengthNeeded / numberOfGlass;

  const pannels: Pannel[] = [];
  let doorsNeeded = line.numberOfDoors;
  const [lowerBoundR, upperBoundR] = calculateBounds(idealGlass, r48List);
  const [lowerBound, upperBound] = calculateBounds(idealGlass, h48List);
  const numberOfIdealGlassNeeded = calculateCombinationIdeal(
    numberOfGlass,
    lowerBound ? lowerBound.length : 0,
    upperBound ? upperBound.length : 0,
    totalLengthNeeded
  );

  function addPanelsToCombination(
    bound: Pannel,
    boundR: Pannel | null,
    numberOfPanels: number
  ) {
    for (let i = 0; i < numberOfPanels; i += 1) {
      if (doorsNeeded > 0) {
        pannels.push(boundR ? boundR : bound);
        doorsNeeded--;
      } else {
        pannels.push(bound);
      }
    }
  }

  if (lowerBound) {
    addPanelsToCombination(lowerBound, lowerBoundR, numberOfIdealGlassNeeded);
  }

  if (upperBound) {
    const remainingPanels = numberOfGlass - numberOfIdealGlassNeeded;
    addPanelsToCombination(upperBound, upperBoundR, remainingPanels);
  }

  return pannels;
}

function getPannels(
  line: Line,
  scale: number,
  glassDeduction: number,
  glassList: Pannel[],
  gapSize: number,
  companyConfig: CompanyConfig
): Pannel[] {
  const h48List = companyConfig.catalog.glassPanels.h48;
  const rOtherList = companyConfig.catalog.glassPanels.rOther;

  if (
    glassList[glassList.length - 1].price ===
    h48List[h48List.length - 1].price
  ) {
    return get48Pannels(line, scale, glassDeduction, gapSize, companyConfig);
  }

  const length = calculateLineLength(line, scale);
  if (!length) {
    throw Error("length is undefined");
  }

  let tempLength = length - companyConfig.catalog.doorWidth * line.numberOfDoors;
  const pannels: Pannel[] = [];
  let doorsNeeded = line.numberOfDoors;

  while (doorsNeeded > 0) {
    pannels.push(rOtherList[0]);
    tempLength = tempLength - rOtherList[0].length;
    doorsNeeded--;
  }

  const numberOfGlass = calculateNumberOfGlassBasedOnBiggestGlass(
    tempLength,
    companyConfig.rules.nonDoorMaxGlassLength
  );
  const spaceNeeded = numberOfGlass - 1;
  const deduction = spaceNeeded * gapSize + glassDeduction;
  const totalLengthNeeded = tempLength - deduction;
  const idealGlass = totalLengthNeeded / numberOfGlass;
  const [lowerBound, upperBound] = calculateBounds(idealGlass, glassList);
  const numberOfIdealGlassNeeded = calculateCombinationIdeal(
    numberOfGlass,
    lowerBound ? lowerBound.length : 0,
    upperBound ? upperBound.length : 0,
    totalLengthNeeded
  );

  if (lowerBound) {
    for (let i = 0; i < numberOfIdealGlassNeeded; i += 1) {
      pannels.push(lowerBound);
    }
  }

  if (upperBound) {
    for (let i = 0; i < numberOfGlass - numberOfIdealGlassNeeded; i += 1) {
      pannels.push(upperBound);
    }
  }

  return pannels;
}

export function getPannelList(localselectedOption: number): Pannel[] {
  const companyConfig = getActiveCompanyConfig();
  return getPannelListForCompany(localselectedOption, companyConfig);
}

export function getPannelListForCompany(
  localselectedOption: number,
  companyConfig: CompanyConfig
): Pannel[] {
  const { h36, h42, h48 } = companyConfig.catalog.glassPanels;
  if (localselectedOption === 42) {
    return h42;
  }
  if (localselectedOption === 36) {
    return h36;
  }
  return h48;
}

export function getCost(lines: Line[], context: Context): Pannel[] {
  const companyConfig = getActiveCompanyConfig();
  return getCostForCompany(lines, context, companyConfig);
}

export function getCostForCompany(
  lines: Line[],
  context: Context,
  companyConfig: CompanyConfig
): Pannel[] {
  const pannels: Pannel[][] = [];

  if (!lines || lines.length === 0) {
    return [];
  }

  let gap = context.gapSize || companyConfig.rules.defaultGapSize;
  if (!context.isChalkLine) {
    gap = companyConfig.rules.defaultGapSize;
  }

  const sharedLines = getAllIntersections(
    lines,
    companyConfig.rules.pointEqualityThresholdPx
  );
  const substractions = findSubstractions(sharedLines);

  for (let i = 0; i < substractions.length; i++) {
    const pannel = getPannels(
      lines[i],
      context.scale,
      context.isChalkLine
        ? companyConfig.rules.chalkLineDeductionMultiplier * gap
        : substractions[i].substractions,
      context.glassList,
      gap,
      companyConfig
    );

    if (pannel) {
      lines[i].pannels = pannel;
      pannels.push(pannel);
    }
  }

  return pannels.flat();
}
